import React, { useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PortfolioImageGalleryContainer from 'containers/PortfolioImageGalleryContainer'
import { useParams } from 'react-router-dom'
import { Button } from 'components/ui/button'
import { Upload } from 'lucide-react'
import ImageUploadWizard, {
	commitImageUpload,
	portfolioImageAdapter,
} from 'components/ImageUploadWizard'
import LocationImagesEmptyState from 'components/LocationImagesEmptyState'
import Spinner from 'components/Spinner'
import Message from 'components/Message'

/**
 * Legacy portfolio Images screen, still mounted for partner users via
 * getPortfolioPartnerRoutes. Kept in step with PortfolioImagesCN so both share
 * the same upload flow.
 */
const PortfolioImagesScreen = () => {
	const { id } = useParams()
	const dispatch = useDispatch()

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo } = userLogin

	const [wizardOpen, setWizardOpen] = useState(false)

	const { isLoading: loading, portfolio, error } = useSelector(state => {
		return {
			isLoading: state.portfolios.isLoading,
			portfolio: state.portfolios.entities[id] || {},
			error: state.portfolios.error,
		}
	})

	const uploadTarget = useMemo(
		() => ({
			entityLabel: portfolio?.portfolioName || 'this portfolio',
			folder: `mdt/portfolios/${id}`,
			onCommit: (commit, state) =>
				commitImageUpload(
					commit,
					portfolioImageAdapter({ dispatch, userId: userInfo?._id, portfolioId: id }),
					{},
					state,
				),
		}),
		[dispatch, userInfo?._id, id, portfolio?.portfolioName],
	)

	if (loading) return <Spinner containerHeight="50vh" />
	if (error) return <Message variant="danger">{error}</Message>

	const hasImages = portfolio?.images?.length > 0

	return (
		<div>
			{hasImages && (
				<div className="mb-6 flex flex-wrap items-center justify-between gap-4">
					<div className="flex flex-col gap-1">
						<h2 className="m-0 text-2xl font-bold">Images</h2>
						<span className="text-[0.95rem] text-muted-foreground">
							Maintain a visual record of this portfolio with organized photo uploads.
						</span>
					</div>
					<Button type="button" variant="primary" onClick={() => setWizardOpen(true)}>
						<span className="flex items-center gap-2">
							<Upload className="h-4 w-4" />
							Upload Images
						</span>
					</Button>
				</div>
			)}

			{hasImages ? (
				<PortfolioImageGalleryContainer onUploadClick={() => setWizardOpen(true)} />
			) : (
				<LocationImagesEmptyState onUploadClick={() => setWizardOpen(true)} />
			)}

			<ImageUploadWizard
				open={wizardOpen}
				onClose={() => setWizardOpen(false)}
				target={uploadTarget}
			/>
		</div>
	)
}

export default PortfolioImagesScreen
